/**
 * Email-related utility functions
 */

import * as Api from '@ViewModels';
import FortyTwoFloorsIconUrl from '../../web/assets/42floors.png';
import AolLogoUrl from '../../web/assets/aol_logo.png';
import CrexiIconUrl from '../../web/assets/crexi.png';
import DatalotUrl from '../../web/assets/datalot.png';
import elpUrl from '../../web/assets/elp.png';
import GenericEmailLogoUrl from '../../web/assets/email_graphic.svg';
import EverquoteUrl from '../../web/assets/Everquote.png';
import ExchangeLogoUrl from '../../web/assets/exchange_logo.png';
import FiveStreetIconUrl from '../../web/assets/five_street.png';
import FollowuUpBossIconUrl from '../../web/assets/followupboss.png';
import Forge3IconUrl from '../../web/assets/forge3.png';
import GoogleLogoUrl from '../../web/assets/google_logo_large.png';
import HomeLightIconUrl from '../../web/assets/homelight.png';
import HomesComIconUrl from '../../web/assets/homescom.svg';
import IDXBrokerIconUrl from '../../web/assets/IDX.png';
import IHomeFinderIconUrl from '../../web/assets/ihomefinder.jpg';
import IHouseWebIconUrl from '../../web/assets/ihouseweb.png';
import KeapIconUrl from '../../web/assets/keap.svg';
import LassoCRMIconUrl from '../../web/assets/lasso.png';
import NOLOIconUrl from '../../web/assets/logo_nolo.svg';
import LoopNetIconUrl from '../../web/assets/loopnet.svg';
import O365LogoUrl from '../../web/assets/office_365_logo.png';
import OpcityIconUrl from '../../web/assets/opcity.png';
import PlacesterUrl from '../../web/assets/placester.png';
import RealtorIconUrl from '../../web/assets/realtor.png';
import ReferralExchangeIconUrl from '../../web/assets/referralexchange.png';
import SmartVestorUrl from '../../web/assets/smartvestor.jpg';
import SpacioIconUrl from '../../web/assets/spacio.png';
import TrustedChoiceUrl from '../../web/assets/trustedchoice.png';
import WizardCallsUrl from '../../web/assets/wizardcalls.png';
import YahooLogoUrl from '../../web/assets/yahoo_logo.png';
import ZBuyerIconUrl from '../../web/assets/zbuyer.png';
import ZillowIconUrl from '../../web/assets/zillow.png';

/**
 * Gets normalized email provider
 * @source Original location: UiUtils.ts.backup line 296
 */
export const getNormalizedEmailProvider = (emailProvider?: Api.EmailProviderType): Api.EmailProviderType => {
	if (emailProvider == null) {
		return null;
	}

	switch (emailProvider) {
		case 'Google':
		case 'Office365':
		case 'Gmail':
		case 'Exchange':
		case 'Imap':
		case 'Misc':
		case 'Yahoo':
		case 'Aol':
		default:
			return emailProvider;
	}
};

/**
 * Gets email provider logo URL
 * @source Original location: UiUtils.ts.backup line 335
 */
export const getEmailProviderLogoUrl = (emailProvider?: Api.EmailProviderType) => {
	const normalizedEmailProvider = getNormalizedEmailProvider(emailProvider);
	if (!normalizedEmailProvider) {
		return null;
	}

	if (normalizedEmailProvider === 'Google' || normalizedEmailProvider === 'Gmail') {
		return GoogleLogoUrl;
	} else if (normalizedEmailProvider === 'Office365' || normalizedEmailProvider === 'Outlook') {
		return O365LogoUrl;
	} else if (normalizedEmailProvider === 'Exchange') {
		return ExchangeLogoUrl;
	} else if (normalizedEmailProvider === 'Yahoo') {
		return YahooLogoUrl;
	} else if (normalizedEmailProvider === 'Aol') {
		return AolLogoUrl;
	}

	return GenericEmailLogoUrl;
};

/**
 * Gets email provider display name
 * @source Original location: UiUtils.ts.backup line 356
 */
export const getEmailProviderDisplayName = (emailProvider?: Api.EmailProviderType) => {
	const normalizedEmailProvider = getNormalizedEmailProvider(emailProvider);
	if (normalizedEmailProvider == null) {
		return '';
	}

	let emailProviderName = '';
	switch (normalizedEmailProvider) {
		case 'Google':
			emailProviderName = 'Google Workspace';
			break;
		case 'Office365':
			emailProviderName = 'Office 365';
			break;
		case 'Gmail':
			emailProviderName = 'Gmail';
			break;
		case 'Exchange':
			emailProviderName = 'Exchange Server';
			break;
		case 'Imap':
			emailProviderName = 'email';
			break;
		case 'Yahoo':
			emailProviderName = 'Yahoo';
			break;
		case 'Aol':
			emailProviderName = 'AOL';
			break;
		case 'Misc':
		default:
			emailProviderName = '';
			break;
	}

	return emailProviderName;
};

/**
 * Gets email scanner image URL
 * @source Original location: UiUtils.ts.backup line 1119
 */
export const emailScannerImageUrl = (id: Api.EmailScannerId) => {
	switch (id.toLocaleLowerCase()) {
		case Api.EmailScannerId.Crexi.toLocaleLowerCase():
			return CrexiIconUrl;
		case Api.EmailScannerId.FiveStreet.toLocaleLowerCase():
			return FiveStreetIconUrl;
		case Api.EmailScannerId.RealtorDotCom.toLocaleLowerCase():
			return RealtorIconUrl;
		case Api.EmailScannerId.Zillow.toLocaleLowerCase():
			return ZillowIconUrl;
		case Api.EmailScannerId.IHomefinder.toLocaleLowerCase():
			return IHomeFinderIconUrl;
		case Api.EmailScannerId.IHouseWeb.toLocaleLowerCase():
			return IHouseWebIconUrl;
		case Api.EmailScannerId.Spacio.toLocaleLowerCase():
			return SpacioIconUrl;
		case Api.EmailScannerId.ReferralExchange.toLocaleLowerCase():
			return ReferralExchangeIconUrl;
		case Api.EmailScannerId.Opcity.toLocaleLowerCase():
			return OpcityIconUrl;
		case Api.EmailScannerId.LoopNet.toLocaleLowerCase():
			return LoopNetIconUrl;
		case Api.EmailScannerId.ZBuyer.toLocaleLowerCase():
			return ZBuyerIconUrl;
		case Api.EmailScannerId.FollowUpBoss.toLocaleLowerCase():
			return FollowuUpBossIconUrl;
		case Api.EmailScannerId.HomesConnect.toLocaleLowerCase():
			return HomesComIconUrl;
		case Api.EmailScannerId.LassoCRM.toLocaleLowerCase():
			return LassoCRMIconUrl;
		case Api.EmailScannerId.Keap.toLocaleLowerCase():
			return KeapIconUrl;
		case Api.EmailScannerId.FortyTwoStores.toLocaleLowerCase():
			return FortyTwoFloorsIconUrl;
		case Api.EmailScannerId.HomeLight.toLocaleLowerCase():
			return HomeLightIconUrl;
		case Api.EmailScannerId.IDXBroker.toLocaleLowerCase():
			return IDXBrokerIconUrl;
		case Api.EmailScannerId.EndorsedLocalProviders.toLocaleLowerCase():
			return elpUrl;
		case Api.EmailScannerId.TrustedChoice.toLocaleLowerCase():
			return TrustedChoiceUrl;
		case Api.EmailScannerId.WizardCalls.toLocaleLowerCase():
			return WizardCallsUrl;
		case Api.EmailScannerId.Everquote.toLocaleLowerCase():
			return EverquoteUrl;
		case Api.EmailScannerId.Datalot.toLocaleLowerCase():
			return DatalotUrl;
		case Api.EmailScannerId.SmartVestor.toLocaleLowerCase():
			return SmartVestorUrl;
		case Api.EmailScannerId.Placester.toLocaleLowerCase():
			return PlacesterUrl;
		case Api.EmailScannerId.NOLO.toLocaleLowerCase():
			return NOLOIconUrl;
		case Api.EmailScannerId.Forge3.toLocaleLowerCase():
			return Forge3IconUrl;
		default:
			break;
	}
	return null;
};
